import React, { FunctionComponent, useState } from 'react'

import { useField } from 'formik'
import styled from 'styled-components'
import { SpaceProps, WidthProps } from 'styled-system'

import { RichTextEditor } from '@/atoms/rich-text-editor'

import FieldWrapper from './field-wrapper'

export interface RichTextProps {
  label?: string
  fieldProps?: SpaceProps & WidthProps
  name?: string
  maxHeight?: string
  tooltip?: string
}

// Quill inflates HTML with entities and whitespace (e.g. 2000 chars → 5000+).
// Sanitize before storing to reduce size significantly.
const sanitizeQuillHtml = (html: string): string =>
  html
    .replace(/&nbsp;/g, ' ') // HTML non-breaking space entity → regular space
    .replace(/\u00A0/g, ' ') // Unicode non-breaking space char → regular space
    .replace(/&#39;/g, "'") // HTML apostrophe entity → plain apostrophe
    .replace(/>\s+</g, '><') // Whitespace between tags (never meaningful in Quill output)
    .trim()

const RichTextField: FunctionComponent<RichTextProps> = ({ label, fieldProps, name, maxHeight, tooltip }: RichTextProps) => {
  const [field, meta, helpers] = useField(name)
  const [quillValue, setQuillValue] = useState(field.value || '')

  const handleChange = (value: string): void => {
    setQuillValue(value)
    const normalized = sanitizeQuillHtml(value)
    // Strip all tags and check if any visible content remains
    const hasContent = normalized.replace(/<[^>]*>/g, '').trim() !== ''
    helpers?.setValue(hasContent ? normalized : '')
  }

  return (
    <FieldWrapper id={field.name} meta={meta} label={label} fieldProps={fieldProps} tooltip={tooltip}>
      <EditorWrapper>
        <RichTextEditor value={quillValue} onChange={handleChange} maxHeight={maxHeight} />
      </EditorWrapper>
    </FieldWrapper>
  )
}

const EditorWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.greyLight};
`

RichTextField.displayName = 'RichTextField'

export default RichTextField
